import React, { useEffect, FC } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  PreviewCard,
  BlockBetween,
  Button,
  Icon,
} from "../../../components/Component";
import { Steps, Step } from "react-step-builder";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import SiteStructureHeader from "./steps/SiteStructureHeader";
import BasicInformation from "./steps/BasicInformation";
import StructureConfiguration from "./steps/StructureConfiguration";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import Swal from "sweetalert2";

const config = {
  before:
    SiteStructureHeader as React.ComponentType<{}> as () => React.ReactElement,
};

// Define interfaces for the payload structure
interface SubUnit {
  subUnitName: string;
  unit: number;
  floorNo?: string;
  floorType?: string;
  description: string;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
}

interface Unit {
  unitName: string;
  unitQty: number;
  unitUomId: number;
  isChildExist: boolean;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  description: string;
  noOfFloors?: number;
  unitPerFloor?: number;
  noOfUnitsPerFloor?: number;
  subUnits: SubUnit[];
}

interface ApiPayload {
  siteId: number;
  projectName: string;
  projectTypeId: number;
  units: Unit[];
  siteStructureSequenceId?: string;
}

const SiteMasterFormImproved: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      status: "Active",
      captureFlatNumbers: "NO",
      captureShopNumbers: "NO",
      captureMilestones: "NO",
      villas: [],
      towers: [],
      blocks: [],
      phases: [],
    },
  });

  const backList = () => {
    navigate(`/sitebreakdown-master`);
  };

  // Transform form data to match API payload structure
  const transformFormDataToApiPayload = (formData: any): ApiPayload => {
    const payload: ApiPayload = {
      siteId: parseInt(formData.siteName),
      projectName: formData.projectName,
      projectTypeId: parseInt(formData.projectType),
      units: [],
    };

    // Helper function to transform date to YYYY-MM-DD format
    const formatDate = (date: any): string | null => {
      if (!date) return null;
      if (typeof date === "string") {
        return date.split("T")[0];
      }
      if (date instanceof Date) {
        return date.toISOString().split("T")[0];
      }
      if (date && typeof date === "object" && date.format) {
        return date.format("YYYY-MM-DD");
      }
      return null;
    };

    // Transform based on project type
    switch (formData.projectType) {
      case "1": // Villa
        if (formData.siteStructures?.length > 0) {
          payload.units = formData.siteStructures.map((unit: any) => {
            const villaUnit: Unit = {
              unitName: unit.unitName,
              unitQty: parseFloat(unit.unitQty) || 0,
              unitUomId: 1,
              isChildExist: false,
              plannedStartDate: formatDate(unit.plannedStartDate),
              plannedEndDate: formatDate(unit.plannedEndDate),
              description: unit.description || `Villa ${unit.unitName}`,
              subUnits: [],
            };
            return villaUnit;
          });
        }
        break;

      case "2": // Highrise
      case "3": // Standalone
        if (formData.siteStructures?.length > 0) {
          payload.units = formData.siteStructures.map((unit: any) => {
            const isChildExist =
              Array.isArray(unit.subUnits) && unit.subUnits.length > 0;

            const transformedUnit: Unit = {
              unitName: unit.unitName,
              unitQty: parseFloat(unit.unitQty) || 0,
              unitUomId: 1,
              isChildExist,
              plannedStartDate: formatDate(unit.plannedStartDate),
              plannedEndDate: formatDate(unit.plannedEndDate),
              description: unit.description || `${unit.unitName}`,
              noOfFloors: parseInt(unit.noOfFloors) || 0,
              unitPerFloor: parseInt(unit.unitPerFloor) || 0,
              noOfUnitsPerFloor: parseInt(unit.unitPerFloor) || 0,
              subUnits: [],
            };

            if (isChildExist) {
              transformedUnit.subUnits = unit.subUnits.map((s: any) => ({
                subUnitName: s.subUnitName,
                unit: parseFloat(s.unit) || 0,
                floorNo: s.floorNo || "",
                floorType: s.floorType || "residential",
                description:
                  s.description || `${s.subUnitName} in ${unit.unitName}`,
                plannedStartDate: formatDate(s.plannedStartDate),
                plannedEndDate: formatDate(s.plannedEndDate),
              }));
            }

            return transformedUnit;
          });
        }
        break;

      case "4": // Commercial
        if (formData.siteStructures?.length > 0) {
          payload.units = formData.siteStructures.map((unit: any) => {
            const isChildExist =
              Array.isArray(unit.subUnits) && unit.subUnits.length > 0;

            const transformedUnit: Unit = {
              unitName: unit.unitName,
              unitQty: parseFloat(unit.unitQty) || 0,
              unitUomId: 1,
              isChildExist,
              plannedStartDate: formatDate(unit.plannedStartDate),
              plannedEndDate: formatDate(unit.plannedEndDate),
              description: unit.description || `${unit.unitName}`,
              noOfFloors: parseInt(unit.noOfFloors) || 0,
              unitPerFloor: parseInt(unit.unitPerFloor) || 0,
              noOfUnitsPerFloor: parseInt(unit.unitPerFloor) || 0,
              subUnits: [],
            };

            if (isChildExist) {
              transformedUnit.subUnits = unit.subUnits.map((s: any) => ({
                subUnitName: s.subUnitName,
                unit: parseFloat(s.unit) || 0,
                floorNo: s.floorNo || "",
                floorType: s.floorType || "commercial",
                description:
                  s.description || `${s.subUnitName} in ${unit.unitName}`,
                plannedStartDate: formatDate(s.plannedStartDate),
                plannedEndDate: formatDate(s.plannedEndDate),
              }));
            }

            return transformedUnit;
          });
        }
        break;

      case "5": // Infrastructure
        if (formData.siteStructures?.length > 0) {
          payload.units = formData.siteStructures.map((unit: any) => {
            const isChildExist =
              Array.isArray(unit.subUnits) && unit.subUnits.length > 0;

            const transformedUnit: Unit = {
              unitName: unit.unitName,
              unitQty: parseFloat(unit.unitQty) || 0,
              unitUomId: 3,
              isChildExist,
              plannedStartDate: formatDate(unit.plannedStartDate),
              plannedEndDate: formatDate(unit.plannedEndDate),
              description: unit.description || `${unit.unitName}`,
              subUnits: [],
            };

            if (isChildExist) {
              transformedUnit.subUnits = unit.subUnits.map((s: any) => ({
                subUnitName: s.subUnitName,
                unit: parseFloat(s.unit) || 0,
                description: s.description || `${s.subUnitName}`,
                plannedStartDate: formatDate(s.plannedStartDate),
                plannedEndDate: formatDate(s.plannedEndDate),
              }));
            }

            return transformedUnit;
          });
        }
        break;

      default:
        payload.units = [];
        break;
    }

    if (formData.hasSequence === false) {
      payload.siteStructureSequenceId = formData.siteStructureSequenceId;
    }
    return payload;
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      const payload = transformFormDataToApiPayload(formData);
      let response;
      response = await sitebreakdownMasterApis.createSiteBreakDown(payload);

      if (response && response.status) {
        Swal.fire({
          title: "Success!",
          text: "Site structure has been created",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          backList();
        });
      } else {
        throw new Error(response?.message || "Operation failed");
      }
    } catch (error: any) {
      // console.error("Error submitting form:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to submit site structure",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <React.Fragment>
      <Head title={"Create Site Structure"} />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">{"Create Site Structure"}</BlockTitle>
              <BlockDes>
                {
                  "Create site breakdown structure with detailed configuration for villas, towers, blocks, phases, and more"
                }
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                className="btn-dim"
                onClick={() => backList()}
              >
                <Icon name="menu-alt-r"></Icon>
                <span>Back to List</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <PreviewCard className={"cardBox"} bodyClass="card-inner-xl">
            <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
              <FormProvider {...formMethods}>
                <Steps config={config}>
                  <Step component={BasicInformation} />
                  <Step
                    component={StructureConfiguration}
                    onSubmit={handleFormSubmit}
                  />
                </Steps>
              </FormProvider>
            </div>
          </PreviewCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default SiteMasterFormImproved;
